<template>
	<div
		class="w-100 ap-white pa-8 d-flex justify-center amelipro-patient-banner"
		style="position: relative"
	>
		<div
			class="amelipro-patient-banner__content"
			:style="`width: ${ innerWidth };`"
		>
			<div
				class="d-flex"
				:class="breakpointHelper.is('mdAndUp') ? 'justify-space-between align-center' : 'flex-column'"
			>
				<div
					class="amelipro-patient-banner__patient-infos"
					:class="{
						'd-inline-flex align-center flex-wrap': breakpointHelper.is('mdAndUp'),
						'd-flex align-start justify-space-between': breakpointHelper.is('smOnly'),
						'd-block': breakpointHelper.is('xsOnly')
					}"
				>
					<p class="mb-4 mb-md-0 text-h5 amelipro-patient-banner__patient-info__name">
						<span class="text-h3 font-weight-semibold d-block d-sm-inline">
							{{ name }}
						</span>
						né(e)
						<span v-if="hasBirthName">
							{{ birthName }}
						</span>
						le {{ birthdate }}
					</p>

					<AmeliproBtn
						v-if="!noPatientChange"
						class="mb-4 mb-md-0 text-none text-h5 amelipro-patient-banner__patient-change__btn"
						:class="breakpointHelper.isDesktop() ? 'mx-4 ' : ''"
						hover-underline
						text
						@click="emitPatientChangeEvent"
					>
						{{ locales.patientChange }}

						<template #icon>
							{{ mdiChevronRight }}
						</template>
					</AmeliproBtn>
				</div>

				<p
					class="mb-0 text-h5 ap-blue--text text--darken-1 amelipro-patient-banner__patient-info__doctor"
				>
					{{ patientDoctorInfos }}
				</p>
			</div>

			<hr class="patient-banner__separator">

			<div
				class="d-flex patient-banner-main"
				:class="{
					'justify-space-between align-center': breakpointHelper.is('mdAndUp') && !noMoreInformation,
					'align-center': breakpointHelper.is('mdAndUp') && noMoreInformation,
					'flex-column align-start': breakpointHelper.is('smAndDown'),
				}"
			>
				<div class="d-flex flex-column flex-sm-row">
					<div :class="breakpointHelper.isDesktop() ? 'mr-16' : ''">
						<p class="mb-4 text-h5 font-weight-regular">
							{{ locales.status }}
							<span class="font-weight-semibold">
								{{ patientStatus }}
							</span>
						</p>

						<p class="mb-4 mb-md-0 text-h5 font-weight-regular">
							{{ locales.nir }}
							<span class="font-weight-semibold">
								{{ patientNir }}
							</span>
						</p>
					</div>

					<div :class="breakpointHelper.isDesktop() ? 'mr-3' : ''">
						<p class="mb-4 text-h5">
							{{ patientSystem }}
						</p>

						<p class="mb-4 mb-md-0 text-h5">
							{{ patientOrganism }}
						</p>
					</div>
				</div>

				<AmeliproBtn
					v-if="!noMoreInformation"
					bordered
					color="ap-white"
					hover-color="ap-blue lighten-3"
					:href="moreInformationHref"
					text-color="ap-blue darken-1"
					:to="moreInformationTo"
				>
					{{ locales.more }}
				</AmeliproBtn>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
	import { PropType, computed } from 'vue';
	import AmeliproBtn from '../AmeliproBtn';
	import BreakpointHelper from '../../helpers/BreakpointHelper';
	import { RawLocation } from 'vue-router';
	import { locales } from './locales';
	import { mdiChevronRight } from '@mdi/js';

	const props = defineProps({
		birthName: {
			type: String,
			default: undefined,
		},
		birthdate: {
			type: String,
			default: undefined,
		},
		moreInformationHref: {
			type: String,
			default: undefined,
		},
		moreInformationTo: {
			type: [Array, Object, String] as PropType<RawLocation>,
			default: undefined,
		},
		name: {
			type: String,
			default: undefined,
		},
		noMoreInformation: {
			type: Boolean,
			default: false,
		},
		noPatientChange: {
			type: Boolean,
			default: false,
		},
		patientDoctorInfos: {
			type: String,
			default: undefined,
		},
		patientNir: {
			type: String,
			default: undefined,
		},
		patientOrganism: {
			type: String,
			default: undefined,
		},
		patientStatus: {
			type: String,
			default: undefined,
		},
		patientSystem: {
			type: String,
			default: undefined,
		},
	});

	const breakpointHelper = new BreakpointHelper('smAndUp');

	const innerWidth = computed<string>(() => {
		if (breakpointHelper.width >= 1240) {
			return '1144px';
		}
		return breakpointHelper.width >= 1072 ? '980px' : '100%';
	});
	const hasBirthName = computed<boolean>(() => props.birthName !== undefined);

	const emit = defineEmits(['click:patient-change']);
	const emitPatientChangeEvent = (): void => {
		emit('click:patient-change');
	};
</script>

<style lang="scss" scoped>
	.patient-banner__separator {
		height: 3px;
		width: 100%;
		margin: 1rem 0;
		border: 0;
		background-color: $ap-grey-lighten2;
	}
</style>
